import React, { useState, useEffect } from "react";
import "../css/balances.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import autoTable from "jspdf-autotable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Settings2,
  WalletCards,
  BarChart3,
  Lock,
  Unlock,
} from "lucide-react";

const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
const api = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;


export default function Balances() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showOpeningModal, setShowOpeningModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [adjustType, setAdjustType] = useState("debit");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [cashBreakdown, setCashBreakdown] = useState({
    5000: "",
    1000: "",
    500: "",
    100: "",
    50: "",
    20: "",
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportData, setReportData] = useState(null);
const [openingChecked, setOpeningChecked] = useState(false);
const [openingConfirmed, setOpeningConfirmed] = useState(false);
const [openingInput, setOpeningInput] = useState("");


// 🔐 Retrieve stored auth token
const getAuth = () => {
  const raw = localStorage.getItem("rcs_auth") || sessionStorage.getItem("rcs_auth");
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};

const authHeader = () => {
  const s = getAuth();
  return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {};
};
const auth = getAuth();
const user = auth?.user || {};


const fetchOpeningBalances = async () => {
  try {
    const res = await fetch(api("/balances/opening/today"), {
      headers: { "Content-Type": "application/json", ...authHeader() },
    });
    if (!res.ok) throw new Error("Failed to fetch opening balances");
    return await res.json();
  } catch (err) {
    console.error("❌ Error fetching opening balances:", err);
    return [];
  }
};


useEffect(() => {
  const checkOpeningBalances = async () => {
    const openings = await fetchOpeningBalances();
    if (!openings.length) {
      setShowOpeningModal(true);
    }
  };
  checkOpeningBalances();
}, []);


const fetchBalances = async () => {
  try {
    setLoading(true);
    const res = await fetch(api("/balances/today"), {
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("❌ Backend error text:", text);
      throw new Error(`Unexpected response (${res.status})`);
    }

const data = await res.json();
if (!Array.isArray(data)) throw new Error("Unexpected response format");

// 🔹 Merge with today's opening balances
const openings = await fetchOpeningBalances();
const merged = data.map((b) => {
  const match = openings.find((o) => o.account_name === b.account);
  return {
    ...b,
    opening_balance: match ? Number(match.opening_balance || 0) : 0,
  };
});

setBalances(merged);

  } catch (err) {
    console.error("Failed to load balances", err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  fetchBalances(); // ✅ Only fetch balances, no modal here
  const interval = setInterval(fetchBalances, 300000); // 🔄 every 5 minutes
  return () => clearInterval(interval);
}, []);


  const calculateCashTotal = () =>
    Object.entries(cashBreakdown).reduce(
      (sum, [denom, count]) => sum + Number(denom) * (parseInt(count) || 0),
      0
    );

    // 💰 Format numbers as currency with commas
const formatMoney = (num) =>
  Number(num || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


const exportToPDF = () => {
  if (!reportData) return alert("Please generate a report first.");

  const doc = new jsPDF();
  const title =
    reportType === "overall"
      ? "Overall Daily Balance Report"
      : reportType === "detailed"
      ? `Detailed Account Report - ${selectedAccount}`
      : `Account Report - ${selectedAccount}`;

  // 🕒 Timestamp
  const timestamp = new Date().toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // 🧭 Header
  doc.setFontSize(14);
  doc.text("RCS POS SYSTEM", 14, 12);
  doc.setFontSize(12);
  doc.text(title, 14, 20);
  doc.setFontSize(10);
  doc.text(`Generated on: ${timestamp}`, 14, 26);

  // 💰 Helper to format numbers with commas
  const formatMoney = (num) =>
    Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (reportData.type === "detailed") {
    // 🧾 Detailed report table
    const tableData = reportData.transactions.map((t) => [
      new Date(t.date).toLocaleDateString("en-US"),
      t.details || "—",
      formatMoney(t.debit),
      formatMoney(t.credit),
      formatMoney(t.balance),
    ]);

    // ➕ Totals row (with real balance total)
    const totalBalance = formatMoney(
      reportData.totals.debit - reportData.totals.credit
    );
    tableData.push([
      "TOTAL",
      "",
      formatMoney(reportData.totals.debit),
      formatMoney(reportData.totals.credit),
      totalBalance,
    ]);

    // Render table
    autoTable(doc, {
      startY: 32,
      head: [["Date", "Details", "Debit ($)", "Credit ($)", "Balance ($)"]],
      body: tableData,
      didParseCell: (data) => {
        // Highlight the total row
        if (data.row.raw && data.row.raw[0] === "TOTAL") {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 4) {
            data.cell.styles.textColor = [0, 0, 255]; // blue for balance
          }
        }
      },
    });
  } else {
    // 📊 Summary / Account / Overall table
    const tableData = reportData.data.map((b) => {
      const opening = b.opening_balance || 0;
      const standing = opening + (b.debit - b.credit);
      return [
        b.account,
        formatMoney(opening),
        formatMoney(b.debit),
        formatMoney(b.credit),
        formatMoney(standing),
      ];
    });

const totalStandingValue =
  (reportData.totals.opening || 0) +
  (reportData.totals.debit - reportData.totals.credit);

tableData.push([
  "TOTAL",
  formatMoney(reportData.totals.opening),
  formatMoney(reportData.totals.debit),
  formatMoney(reportData.totals.credit),
  formatMoney(totalStandingValue),
]);


    autoTable(doc, {
      startY: 32,
      head: [["Account", "Opening ($)", "Debits ($)", "Credits ($)", "Standing ($)"]],
      body: tableData,
      didParseCell: (data) => {
        if (data.row.raw && data.row.raw[0] === "TOTAL") {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 4) {
            data.cell.styles.textColor = [0, 0, 255];
          }
        }
      },
    });
  }

  // 🧾 Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(10);
  doc.text(
    `RCS ALL RIGHTS RESERVED © ${new Date().getFullYear()}`,
    14,
    pageHeight - 10
  );

  // 💾 Save file
  doc.save(
    `${title.replace(/\s+/g, "_")}_${timestamp.replace(/[,: ]/g, "_")}.pdf`
  );
};

// 📘 Generate automatic overall closing report PDF
const generateClosingPDF = (balances) => {
  const doc = new jsPDF();
  const title = "End of Day Closing Report";

  const timestamp = new Date().toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // 🧭 Header
  doc.setFontSize(14);
  doc.text("RCS POS SYSTEM", 14, 12);
  doc.setFontSize(12);
  doc.text(title, 14, 20);
  doc.setFontSize(10);
  doc.text(`Generated automatically on: ${timestamp}`, 14, 26);

  // 💰 Format helper
  const formatMoney = (num) =>
    Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // 📊 Build data for table
  const tableData = balances.map((b) => {
    const opening = b.opening_balance || 0;
    const standing = opening + (b.debit - b.credit);
    return [
      b.account,
      formatMoney(opening),
      formatMoney(b.debit),
      formatMoney(b.credit),
      formatMoney(standing),
    ];
  });

  // ➕ Totals row
  const totals = balances.reduce(
    (acc, b) => {
      const opening = b.opening_balance || 0;
      acc.opening += opening;
      acc.debit += b.debit;
      acc.credit += b.credit;
      acc.standing += opening + (b.debit - b.credit);
      return acc;
    },
    { opening: 0, debit: 0, credit: 0, standing: 0 }
  );

  tableData.push([
    "TOTAL",
    formatMoney(totals.opening),
    formatMoney(totals.debit),
    formatMoney(totals.credit),
    formatMoney(totals.standing),
  ]);

  // 🧾 Generate table
  autoTable(doc, {
    startY: 32,
    head: [["Account", "Opening ($)", "Debits ($)", "Credits ($)", "Closing ($)"]],
    body: tableData,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === "TOTAL") {
        data.cell.styles.fillColor = [240, 240, 240];
        data.cell.styles.fontStyle = "bold";
        if (data.column.index === 4) data.cell.styles.textColor = [0, 0, 255];
      }
    },
  });

  // 🧾 Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(10);
  doc.text(`RCS ALL RIGHTS RESERVED © ${new Date().getFullYear()}`, 14, pageHeight - 10);

  // 💾 Save file
  const safeDate = new Date().toISOString().split("T")[0];
  doc.save(`RCS_Closing_Report_${safeDate}.pdf`);
};


const exportToCSV = () => {
  if (!reportData) return alert("Please generate a report first.");

  const timestamp = new Date().toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // 💰 Format numbers with commas
  const formatMoney = (num) =>
    Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  let csvData = [];

  if (reportData.type === "detailed") {
    csvData = [
      { Note: "RCS POS SYSTEM" },
      { Note: `Detailed Account Report - ${selectedAccount}` },
      { Note: `Generated on: ${timestamp}` },
      {},
      ...reportData.transactions.map((t) => ({
        Date: new Date(t.date).toLocaleDateString("en-US"),
        Details: t.details || "—",
        "Debit ($)": formatMoney(t.debit),
        "Credit ($)": formatMoney(t.credit),
        "Balance ($)": formatMoney(t.balance),
      })),
      {
        Date: "TOTAL",
        Details: "",
        "Debit ($)": formatMoney(reportData.totals.debit),
        "Credit ($)": formatMoney(reportData.totals.credit),
        "Balance ($)": formatMoney(reportData.totals.debit - reportData.totals.credit),
      },
      {},
      { Note: `RCS ALL RIGHTS RESERVED © ${new Date().getFullYear()}` },
    ];
  } else {
    csvData = [
      { Note: "RCS POS SYSTEM" },
      {
        Note:
          reportType === "overall"
            ? "Overall Daily Balance Report"
            : `Account Report - ${selectedAccount}`,
      },
      { Note: `Generated on: ${timestamp}` },
      {},
      ...reportData.data.map((b) => {
        const opening = b.opening_balance || 0;
        const standing = opening + (b.debit - b.credit);
        return {
          Account: b.account,
          "Opening ($)": formatMoney(opening),
          "Debits ($)": formatMoney(b.debit),
          "Credits ($)": formatMoney(b.credit),
          "Standing ($)": formatMoney(standing),
        };
      }),
      {
        Account: "TOTAL",
        "Opening ($)": formatMoney(reportData.totals.opening),
        "Debits ($)": formatMoney(reportData.totals.debit),
        "Credits ($)": formatMoney(reportData.totals.credit),
        "Standing ($)": formatMoney(reportData.totals.standing),
      },
      {},
      { Note: `RCS ALL RIGHTS RESERVED © ${new Date().getFullYear()}` },
    ];
  }

  const ws = XLSX.utils.json_to_sheet(csvData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");
  const safeTimestamp = timestamp.replace(/[,: ]/g, "_");

  XLSX.writeFile(
    wb,
    `${
      reportType === "detailed"
        ? `Detailed_Report_${selectedAccount}_${safeTimestamp}`
        : reportType === "overall"
        ? `Overall_Report_${safeTimestamp}`
        : `Account_Report_${selectedAccount}_${safeTimestamp}`
    }.csv`
  );
};

const handleOpeningAdjust = async () => {
  try {
    let newOpening = 0;

    if (selectedAccount === "Cash") {
      newOpening = calculateCashTotal();
    } else {
      if (openingBalance === "") {
       toast.warn("⚠️ Please enter an opening balance.", { autoClose: 4000 });
        return;
      }
      newOpening = parseFloat(openingBalance);
    }

    const payload = {
      accounts: [{ account: selectedAccount, opening_balance: newOpening }],
    };

    const res = await fetch(api("/balances/opening"),{
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("❌ Error response:", text);
      throw new Error(`Backend returned ${res.status}`);
    }

   toast.success("✅ Opening balance saved successfully!", { autoClose: 4000 });
    setShowOpeningModal(false);
    setSelectedAccount("");
    setOpeningBalance("");
    setCashBreakdown({
      5000: "",
      1000: "",
      500: "",
      100: "",
      50: "",
      20: "",
    });

    // 🔁 Refresh balances
    fetchBalances();
  } catch (err) {
    console.error("Error adjusting opening balance:", err);
    toast.error(`❌ Failed to save opening balance: ${err.message}`, { autoClose: 5000 });
  }
};


const handleCashAdjustment = async () => {
  if (!adjustAmount || isNaN(adjustAmount)) {
    toast.warn("⚠️ Please enter a valid amount.", { autoClose: 4000 });
    return;
  }

  const amount = parseFloat(adjustAmount);

  try {
    // ✅ Post to backend to persist the change
    const res = await fetch(api("/balances/adjust"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({
        account: "Cash",
        type: adjustType,
        amount,
        details: adjustType === "debit" ? "Cash added manually" : "Cash removed manually",
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Backend returned ${res.status}: ${text}`);
    }

    toast.success("✅ Cash adjustment saved successfully!", { autoClose: 3000 });
    setShowAdjustModal(false);
    setAdjustAmount("");

    // ✅ Refresh table after save
    fetchBalances();
  } catch (err) {
    console.error("❌ Error saving cash adjustment:", err);
    toast.error(`Failed to save cash adjustment: ${err.message}`, { autoClose: 5000 });
  }
};



const handleEndOfDay = async () => {
  if (!window.confirm("Are you sure you want to close the day? This will lock today's balances and create opening balances for tomorrow.")) {
    return;
  }

  try {
    const payload = balances.map((b) => ({
      account: b.account,
      closing_balance: b.opening_balance + (b.debit - b.credit),
    }));

    const res = await fetch(api("/balances/closing"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({ accounts: payload }),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("❌ Error response:", text);
      throw new Error(`Backend returned ${res.status}`);
    }

    // ✅ Notify user of completion
    toast.success("✅ End of day completed! Generating closing report...", {
      autoClose: 4000,
    });

    // 🧾 Automatically generate and download report (no user prompt)
    generateClosingPDF(balances);

    toast.info("📄 Closing report downloaded automatically.", {
      position: "bottom-right",
      autoClose: 5000,
    });

    // 🔄 Reset UI state
    setBalances([]);
    setShowOpeningModal(false);
    setShowAdjustModal(false);
    setShowReportModal(false);
  } catch (err) {
    console.error("Error completing end of day:", err);
    toast.error(`❌ Failed to close the day: ${err.message}`, {
      autoClose: 6000,
    });
  }
};



const handleReopenDay = async () => {
  const reason = prompt("Enter reason for reopening the day:");
  if (!reason) {
    toast.warn("⚠️ Reopen cancelled — reason is required.", { autoClose: 4000 });
    return;
  }

  try {
    const res = await fetch(api("/balances/reopen"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({
        reason,
        user: auth?.user?.email || "Admin",
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text);
    }

    toast.success("✅ Day reopened successfully! You can now make adjustments.", { autoClose: 4000 });
    fetchBalances();
  } catch (err) {
    console.error("Error reopening day:", err);
    toast.error(`❌ Failed to reopen: ${err.message}`, { autoClose: 5000 });
  }
};

return (
  <div className="modern-balances-page">

    {/* =====================================================
        HERO
    ===================================================== */}
    <div className="modern-balances-hero">

      <div className="modern-balances-hero-left">

        <div className="modern-balances-pill">
          Financial Operations
        </div>

        <h1>
          Daily Balances
        </h1>

        <p>
          Monitor opening balances,
          standing balances, account
          movement and operational
          financial activity. ENSURE YOUR OPENING BALANCES ARE ACCURATE!!
        </p>

      </div>

      {/* =====================================================
          LUCIDE ACTIONS
      ===================================================== */}
      <div className="modern-balances-actions">

        {/* OPENING */}
        <button
          className="modern-balance-action modern-balance-action-teal"
          onClick={() =>
            setShowOpeningModal(true)
          }
        >

          <div className="modern-balance-action-icon">
            <Settings2 size={18} />
          </div>

          <div className="modern-balance-action-content">

            <span>
              Opening Balance
            </span>

            <p>
              Adjust opening values
            </p>

          </div>

        </button>

        {/* CASH */}
        <button
          className="modern-balance-action modern-balance-action-blue"
          onClick={() =>
            setShowAdjustModal(true)
          }
        >

          <div className="modern-balance-action-icon">
            <WalletCards size={18} />
          </div>

          <div className="modern-balance-action-content">

            <span>
              Cash Adjustment
            </span>

            <p>
              Add or remove cash
            </p>

          </div>

        </button>

        {/* REPORTS */}
        <button
          className="modern-balance-action modern-balance-action-purple"
          onClick={() =>
            setShowReportModal(true)
          }
        >

          <div className="modern-balance-action-icon">
            <BarChart3 size={18} />
          </div>

          <div className="modern-balance-action-content">

            <span>
              Reports
            </span>

            <p>
              Generate exports
            </p>

          </div>

        </button>

        {/* END DAY */}
        <button
          className="modern-balance-action modern-balance-action-red"
          onClick={handleEndOfDay}
        >

          <div className="modern-balance-action-icon">
            <Lock size={18} />
          </div>

          <div className="modern-balance-action-content">

            <span>
              End Day
            </span>

            <p>
              Lock business period
            </p>

          </div>

        </button>

        {/* REOPEN */}
        {user?.role === "admin" && (

          <button
            className="modern-balance-action modern-balance-action-orange"
            onClick={handleReopenDay}
          >

            <div className="modern-balance-action-icon">
              <Unlock size={18} />
            </div>

            <div className="modern-balance-action-content">

              <span>
                Reopen Day
              </span>

              <p>
                Unlock adjustments
              </p>

            </div>

          </button>

        )}

      </div>

    </div>

    {/* =====================================================
        DATE
    ===================================================== */}
    <div className="modern-balances-date">

      {new Date().toLocaleDateString(
        "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      )}

    </div>

    {/* =====================================================
        SUMMARY GRID
    ===================================================== */}
    {!loading && !error && (

      <div className="modern-balance-summary-grid">

        {/* OPENING */}
        <div className="modern-balance-summary-card">

          <span>
            Total Opening
          </span>

          <h2>
            $
            {formatMoney(
              balances.reduce(
                (s, b) =>
                  s +
                  (b.opening_balance || 0),
                0
              )
            )}
          </h2>

        </div>

        {/* DEBITS */}
        <div className="modern-balance-summary-card">

          <span>
            Total Debits
          </span>

          <h2 className="green">
            $
            {formatMoney(
              balances.reduce(
                (s, b) =>
                  s +
                  (b.debit || 0),
                0
              )
            )}
          </h2>

        </div>

        {/* CREDITS */}
        <div className="modern-balance-summary-card">

          <span>
            Total Credits
          </span>

          <h2 className="red">
            $
            {formatMoney(
              balances.reduce(
                (s, b) =>
                  s +
                  (b.credit || 0),
                0
              )
            )}
          </h2>

        </div>

        {/* STANDING */}
        <div className="modern-balance-summary-card">

          <span>
            Standing Balance
          </span>

          <h2 className="blue">
            $
            {formatMoney(
              balances.reduce(
                (s, b) =>
                  s +
                  (
                    (b.opening_balance || 0) +
                    (b.debit - b.credit)
                  ),
                0
              )
            )}
          </h2>

        </div>

      </div>

    )}

    {/* =====================================================
        TABLE CARD
    ===================================================== */}
    <div className="modern-balances-table-card">

      <div className="modern-balances-card-header">

        <div>

          <h3>
            Balance Ledger
          </h3>

          <span>
            Daily account standing
            and movement overview
          </span>

        </div>

      </div>

      {loading ? (

        <div className="modern-balances-loading">
          Loading balances...
        </div>

      ) : error ? (

        <div className="modern-balances-error">
          Error: {error}
        </div>

      ) : (

        <div className="modern-balances-table-wrap">

          <table className="modern-balances-table">

            <thead>

              <tr>

                <th>
                  Account
                </th>

                <th>
                  Opening
                </th>

                <th>
                  Debits
                </th>

                <th>
                  Credits
                </th>

                <th>
                  Standing
                </th>

              </tr>

            </thead>

            <tbody>

              {balances.map((b) => {

                const opening =
                  b.opening_balance || 0;

                const standing =
                  opening +
                  (b.debit - b.credit);

                return (

                  <tr
                    key={b.account}
                  >

                    <td className="modern-balance-account">
                      {b.account}
                    </td>

                    <td>
                      $
                      {formatMoney(opening)}
                    </td>

                    <td className="green">
                      $
                      {formatMoney(b.debit)}
                    </td>

                    <td className="red">
                      $
                      {formatMoney(b.credit)}
                    </td>

                    <td
                      className={
                        standing >= 0
                          ? "blue"
                          : "red"
                      }
                    >
                      $
                      {formatMoney(standing)}
                    </td>

                  </tr>

                );
              })}

            </tbody>

            {/* =====================================================
                FOOTER TOTALS
            ===================================================== */}
            <tfoot>

              <tr className="modern-balance-total-row">

                <td>
                  TOTAL
                </td>

                <td>
                  $
                  {formatMoney(
                    balances.reduce(
                      (s, b) =>
                        s +
                        (b.opening_balance || 0),
                      0
                    )
                  )}
                </td>

                <td className="green">
                  $
                  {formatMoney(
                    balances.reduce(
                      (s, b) =>
                        s +
                        (b.debit || 0),
                      0
                    )
                  )}
                </td>

                <td className="red">
                  $
                  {formatMoney(
                    balances.reduce(
                      (s, b) =>
                        s +
                        (b.credit || 0),
                      0
                    )
                  )}
                </td>

                <td className="blue">
                  $
                  {formatMoney(
                    balances.reduce(
                      (s, b) =>
                        s +
                        (
                          (b.opening_balance || 0) +
                          (b.debit - b.credit)
                        ),
                      0
                    )
                  )}
                </td>

              </tr>

            </tfoot>

          </table>

        </div>

      )}

    </div>

    {/* =====================================================
        NOTE
    ===================================================== */}
    <div className="modern-balance-note">

      <strong>
        NOTE:
      </strong>

      Opening balances are automatic.
      If balances differ from your
      actual opening values, use the
      Opening Balance adjustment.
      Do not use opening balance
      adjustments to debit or credit
      cash movements.

    </div>

    {/* =====================================================
        OPENING MODAL
    ===================================================== */}
    {showOpeningModal && (

      <div className="modal-overlay">

        <div className="modal wide">

          {!selectedAccount ||
          selectedAccount !== "Cash" ? (
            <>

              <h3>
                Adjust Opening Balance
              </h3>

              <label>
                Account
              </label>

              <select
                value={selectedAccount}
                onChange={(e) =>
                  setSelectedAccount(
                    e.target.value
                  )
                }
              >

                <option value="">
                  Select Account
                </option>

                {balances.map((b) => (

                  <option
                    key={b.account}
                    value={b.account}
                  >
                    {b.account}
                  </option>

                ))}

              </select>

              {selectedAccount &&
                selectedAccount !== "Cash" && (
                  <>
                    <label>
                      Opening Balance
                    </label>

                    <input
                      type="number"
                      placeholder="Enter opening balance"
                      value={openingBalance}
                      onChange={(e) =>
                        setOpeningBalance(
                          e.target.value
                        )
                      }
                    />
                  </>
                )}

              <div className="modal-actions">

                <button
                  onClick={handleOpeningAdjust}
                  className="save-btn"
                >
                  Save
                </button>

                <button
                  onClick={() =>
                    setShowOpeningModal(false)
                  }
                  className="cancel-btn"
                >
                  Cancel
                </button>

              </div>

            </>
          ) : (
            <>

              <h3>
                Cash Breakdown
              </h3>

              <table className="cash-breakdown-table">

                <thead>

                  <tr>

                    <th>
                      Denomination
                    </th>

                    <th>
                      Qty
                    </th>

                    <th>
                      Value
                    </th>

                  </tr>

                </thead>

                <tbody>

                  {Object.keys(cashBreakdown).map(
                    (denom) => {

                      const value =
                        Number(denom) *
                        (
                          parseInt(
                            cashBreakdown[
                              denom
                            ]
                          ) || 0
                        );

                      return (

                        <tr key={denom}>

                          <td>
                            ${denom}
                          </td>

                          <td>

                            <input
                              type="number"
                              min="0"
                              value={
                                cashBreakdown[
                                  denom
                                ]
                              }
                              onChange={(e) =>
                                setCashBreakdown(
                                  (prev) => ({
                                    ...prev,
                                    [denom]:
                                      e.target.value,
                                  })
                                )
                              }
                            />

                          </td>

                          <td>
                            $
                            {value.toFixed(2)}
                          </td>

                        </tr>

                      );
                    }
                  )}

                </tbody>

              </table>

              <div className="modal-actions">

                <button
                  onClick={handleOpeningAdjust}
                  className="save-btn"
                >
                  Confirm
                </button>

                <button
                  onClick={() => {
                    setSelectedAccount("");
                    setShowOpeningModal(false);
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>

              </div>

            </>
          )}

        </div>

      </div>

    )}

    {/* =====================================================
        CASH MODAL
    ===================================================== */}
    {showAdjustModal && (

      <div className="modal-overlay">

        <div className="modal">

          <h3>
            Cash Adjustment
          </h3>

          <label>
            Action
          </label>

          <select
            value={adjustType}
            onChange={(e) =>
              setAdjustType(
                e.target.value
              )
            }
          >

            <option value="debit">
              Add Cash
            </option>

            <option value="credit">
              Remove Cash
            </option>

          </select>

          <label>
            Amount
          </label>

          <input
            type="number"
            placeholder="Enter amount"
            value={adjustAmount}
            onChange={(e) =>
              setAdjustAmount(
                e.target.value
              )
            }
          />

          <div className="modal-actions">

            <button
              onClick={handleCashAdjustment}
              className="save-btn"
            >
              Save
            </button>

            <button
              onClick={() =>
                setShowAdjustModal(false)
              }
              className="cancel-btn"
            >
              Cancel
            </button>

          </div>

        </div>

      </div>

    )}

    {/* =====================================================
        REPORT MODAL
    ===================================================== */}
    {showReportModal && (

      <div className="modal-overlay">

        <div className="modal wide">

          <h3>
            Generate Balance Report
          </h3>

          {/* existing report logic remains */}

        </div>

      </div>

    )}

    <ToastContainer
      position="top-center"
      autoClose={4000}
    />

  </div>
);
}
